// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { TabProps } from "@insite/mobius/Tab";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { xBaseTheme } from "@pacific/XDL/types";
import color from "@pacific/XDL/Utilities/color";
import { styledScrollbar } from "@pacific/XDL/Utilities/scroll";
import { css } from "styled-components";

const tabSelectedStyle = css`
    outline-width: 0;
    background: transparent;
    color: ${color("text.main")};
    border-bottom: 4px solid ${color("common.ui9")};
    &:hover {
        color: ${color("common.ui8")};
        border-bottom: 4px solid ${color("common.ui9")};
    }
`;

const tabStyle = css`
    padding: 0 20px 12px;
    min-width: initial;
    user-select: none;
    position: relative;
    border: none;
    transition: all 0.2s;
    color: ${color("text.main", 0.33)};
    &:hover {
        color: ${color("common.ui8")};
        cursor: pointer;
        border: none;
    }
    &:focus {
        ${tabSelectedStyle}
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        typographyProps: {
            variant: "h5",
            css: css`
                margin-bottom: 0;
            `,
        },
        css: css`
            ${tabStyle}
            ${({ selected }: TabProps) => selected && tabSelectedStyle}
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        cssOverrides: {
            tabGroup: css`
                padding: 0;
                position: relative;
                align-items: flex-start;
                border-bottom: 1px solid ${color("common.border")};
                ${styledScrollbar(4)}
            `,
            tabContent: css`
                margin-top: 0;
                border: none;
                background: transparent;
                box-shadow: none;
                padding: 20px 0 0;
                @media print {
                    padding: 0;
                    margin-bottom: 12px;
                    display: block;

                    & > ${TypographyStyle} {
                        display: inline-block;
                        ${tabStyle}
                        ${tabSelectedStyle}
                    }

                    & > div {
                        padding: 0;
                        border: 1px solid ${color("common.border")};
                    }
                }
            `,
            wrapper: css`
                width: 100%;
            `,
        },
    },
} as xBaseTheme["tab"];
